import { createIsomorphicFn } from '@tanstack/react-start'
import { getRequest } from '@tanstack/react-start/server'

import {
  type ConsentMode,
  classifyConsentMode,
  parseConsentOverride,
} from './consent'

declare global {
  interface Window {
    /** Consent mode injected by the server at request time — see __root.tsx. */
    __ANALYTICS_CONSENT_MODE__?: ConsentMode
  }
}

/**
 * The regional consent default for the current visitor.
 *
 * This has to be known BEFORE `posthog.init()` runs, or an EEA visitor's first
 * pageview is captured before they have agreed to anything. So it is resolved
 * on the server from request headers and inlined into the document (alongside
 * `__DEPLOYMENT_ENV__`, see __root.tsx), not fetched after hydration.
 *
 * `createIsomorphicFn` is what keeps that safe: the Start compiler replaces the
 * whole chain with the branch for the environment being built, so `getRequest`
 * and its server-only dependencies are eliminated from the client bundle.
 *
 * Requires Cloudflare IP Geolocation to be ON for `cf-ipcountry` to be present.
 * When it is missing, `classifyConsentMode` degrades to `accept-language`.
 */
export const consentMode = createIsomorphicFn()
  .server((): ConsentMode => {
    const request = getRequest()
    // `?consent=opt-in|opt-out` forces a mode so both variants of the notice
    // can be looked at from one machine. Off in production, on in staging and
    // local dev, where DEPLOYMENT_ENV is 'staging' or unset. Worst case if the
    // guard ever failed: a visitor could pick their own consent default, which
    // the notice already lets them do.
    if (process.env.DEPLOYMENT_ENV !== 'production') {
      const override = parseConsentOverride(request.url)
      if (override) return override
    }
    const headers = request.headers
    return classifyConsentMode(
      headers.get('cf-ipcountry'),
      headers.get('accept-language'),
    )
  })
  // A missing global means the injection script did not run. Assume the strict
  // default rather than capturing from a visitor whose region is unknown.
  .client((): ConsentMode => window.__ANALYTICS_CONSENT_MODE__ ?? 'opt-in')
